import {
  Sparkles,
  Cloud,
  GitBranch,
  BarChart3,
  Code2,
  Shield,
  Globe,
  Layers,
  Server,
  Lock,
  Cpu,
  Zap,
  Compass,
  Users,
  BookOpen,
  Network,
  Heart,
  Bot,
  Wallet,
  Database,
} from "lucide-react";

/* =====================================================================
   STATIC PAGES — single source of truth for the footer + their routes
   ===================================================================== */

export const STATIC_PAGES = [
  /* ============================= PRODUCT ============================= */
  {
    path: "/product/overview",
    section: "Product",
    title: "Overview",
    label: "Overview",
    column: "product",
    description:
      "Plan your cloud infrastructure with AI — from a one-sentence description to a reviewable architecture draft in minutes.",
    cta: { label: "Admin sign in", to: "/login" },
    sections: [
      {
        type: "features",
        kicker: "EVERYTHING IN ONE PLACE",
        title: "The complete cloud planning workflow",
        items: [
          { icon: Bot, title: "AI requirement gathering", body: "Chat naturally about what you're building. OpenAI turns your description into a structured spec — scale, data needs, compliance, budget." },
          { icon: GitBranch, title: "Interactive architecture canvas", body: "Tier-grouped nodes with real provider service names, drag-to-position, click-to-connect edges, auto-save." },
          { icon: BarChart3, title: "Real cost estimates", body: "Per-service monthly cost with low / expected / high ranges, instant provider switch (AZ / AW / GC), side-by-side compare." },
          { icon: Code2, title: "One-click IaC export", body: "Terraform starter, Bicep templates, Markdown checklist, and PNG diagram — all ready for your repo." },
          { icon: Shield, title: "Azure Enterprise-Scale", body: "Generate full CAF landing zones — management groups, subscriptions, RGs, hub-and-spoke, Azure Policy assignments." },
          { icon: Layers, title: "17+ templates", body: "Pre-built blueprints for SaaS, AI services, data pipelines, e-commerce, and more — fork and customize." },
        ],
      },
      {
        type: "prose",
        kicker: "WHY GUIDED SENTRY",
        title: "No blank canvas. No guessing.",
        body: [
          "Most cloud planning tools assume you already know which services you need. Guided Sentry inverts that: describe the product you're building in plain English, answer a few questions about scale and budget, and we recommend an architecture — with cost estimates and review-required IaC exports.",
          "Switch providers and compare costs. Generate an Enterprise-Scale landing-zone draft, then review its resources and Terraform before deploying.",
        ],
      },
    ],
  },
  {
    path: "/product/architecture-templates",
    section: "Product",
    title: "Architecture Templates",
    label: "Architecture Templates",
    column: "product",
    description: "17+ pre-built blueprints — fork and customize in seconds.",
    cta: { label: "Admin sign in", to: "/login" },
    sections: [
      {
        type: "features",
        kicker: "POPULAR TEMPLATES",
        title: "Battle-tested blueprints for every workload",
        columns: 3,
        items: [
          { icon: Globe, title: "B2B SaaS", body: "Web app + worker + Postgres + Redis + auth. The vast majority of new SaaS launches.", tag: "Most popular" },
          { icon: Bot, title: "AI Service", body: "API gateway + model inference + vector DB + object storage + autoscaling GPU pool." },
          { icon: Database, title: "Data Pipeline", body: "Ingestion + stream processing + warehouse + BI — with cost optimisation for batch workloads." },
          { icon: Server, title: "Mobile API", body: "Lightweight REST API + managed Postgres + CDN + push notifications." },
          { icon: Cpu, title: "Real-time", body: "WebSocket gateway + pub/sub + state cache + horizontal autoscaling for low-latency apps." },
          { icon: Lock, title: "Compliance-first", body: "HIPAA / SOC 2 baseline — private endpoints, KMS, audit logs, network isolation." },
        ],
      },
      {
        type: "prose",
        kicker: "HOW TEMPLATES WORK",
        title: "Start fast, customize freely",
        body: [
          "Every template is a fully-rendered architecture with realistic services, sized for a typical workload at the selected scale tier. Fork one and you're dropped straight into the canvas — drag nodes, swap services, switch providers, regenerate costs.",
          "Templates are also a great way to learn: open any one, click a node, and read the AI explanation of why that service was chosen and what the alternatives are.",
        ],
      },
    ],
  },
  {
    path: "/product/cost-estimator",
    section: "Product",
    title: "Cost Estimator",
    label: "Cost Estimator",
    column: "product",
    description: "Real-time monthly cost estimates across Azure, AWS, and GCP — broken down per service.",
    cta: { label: "Admin sign in", to: "/login" },
    sections: [
      {
        type: "features",
        kicker: "WHAT YOU GET",
        title: "Costs grounded in reality",
        items: [
          { icon: Wallet, title: "Per-service breakdown", body: "Every node in your architecture has its own line item with a realistic monthly baseline." },
          { icon: BarChart3, title: "Low / expected / high", body: "Each estimate ships with a range and an explanation of what drives the variance (traffic, storage, egress)." },
          { icon: Compass, title: "Provider compare", body: "Re-price the entire architecture for Azure, AWS, and GCP side-by-side with a single click." },
          { icon: Zap, title: "Budget-aware", body: "Set a monthly budget and we tune every service tier to stay within it without sacrificing the architecture." },
        ],
      },
      {
        type: "prose",
        kicker: "PRICING METHODOLOGY",
        title: "How we calculate",
        body: [
          "Estimates use the current public pricing of each provider's representative SKU for the workload's scale tier — e.g. an App Service B1 instance at starter, P1v3 at scale. We assume 730 hours/month and typical egress patterns for the workload class.",
          "Real-world costs vary based on traffic shape, reserved instance commitments, and discounting — which is why every line ships with a low/high range, not a single number.",
        ],
      },
    ],
  },
  {
    path: "/product/iac-export",
    section: "Product",
    title: "IaC Export",
    label: "IaC Export",
    column: "product",
    description: "Download Terraform and Bicep drafts with a provisioning checklist. Review TODO stubs and validate before deployment.",
    cta: { label: "Admin sign in", to: "/login" },
    sections: [
      {
        type: "features",
        kicker: "EVERY EXPORT YOU NEED",
        title: "From diagram to reviewable IaC",
        columns: 2,
        items: [
          { icon: Code2, title: "Terraform (.tf)", body: "Provider-aware blocks with realistic resource types. For Enterprise-Scale projects, includes `azurerm_management_group`, RGs, resources, and policy assignments." },
          { icon: Cloud, title: "Bicep (Azure)", body: "Azure resource drafts for review and validation before running `az deployment`." },
          { icon: BookOpen, title: "Markdown checklist", body: "Tier-grouped provisioning checklist with cost annotations — perfect for tickets, runbooks, and review handoffs." },
          { icon: GitBranch, title: "PNG / SVG diagram", body: "High-DPI server-rendered diagram with nodes, edges, and the official Azure icon set. Ready for slides and docs." },
        ],
      },
      {
        type: "code",
        kicker: "EXAMPLE",
        title: "What you actually download",
        language: "hcl",
        body: `# Terraform starter generated by Guided Sentry
terraform { required_version = ">= 1.5.0" }
provider "azurerm" { features {} }

resource "azurerm_resource_group" "rg_e2e_compute_prod_eus" {
  name     = "rg-e2e-compute-prod-eus"
  location = "eastus"
  tags = { tier = "compute" }
}

resource "azurerm_linux_web_app" "app_e2e_api" {
  name                = "app-e2e-api"
  resource_group_name = azurerm_resource_group.rg_e2e_compute_prod_eus.name
  location            = azurerm_resource_group.rg_e2e_compute_prod_eus.location
  tags = { monthly_cost_usd = "55" }
}`,
      },
    ],
  },

  /* ============================= RESOURCES ============================= */
  {
    path: "/resources/docs",
    section: "Resources",
    title: "Documentation",
    label: "Docs",
    column: "resources",
    description: "Step-by-step guides covering everything from your first architecture to enterprise CAF landing zones.",
    sections: [
      {
        type: "features",
        kicker: "GET STARTED",
        title: "Pick where you are",
        items: [
          { icon: Sparkles, title: "Your first architecture", body: "Run the requirements wizard, pick a provider, generate, and walk the canvas. About 5 minutes.", tag: "Beginner" },
          { icon: GitBranch, title: "Working with the canvas", body: "Drag nodes, connect edges, swap services from the detail panel, auto-save behavior.", tag: "Beginner" },
          { icon: Compass, title: "Comparing providers", body: "Switch providers, re-price with OpenAI, read the side-by-side compare modal.", tag: "Intermediate" },
          { icon: Layers, title: "Azure Enterprise-Scale", body: "Generate a full CAF landing zone — MGs, subscriptions, RGs, networking, policies.", tag: "Advanced" },
          { icon: Code2, title: "IaC export pipeline", body: "After review and validation, use your own GitHub Actions or Azure DevOps deployment pipeline.", tag: "Advanced" },
          { icon: Lock, title: "Security & governance", body: "How we generate Azure Policy assignments and how to extend them.", tag: "Advanced" },
        ],
      },
      {
        type: "faq",
        items: [
          { q: "Does Guided Sentry deploy infrastructure for me?", a: "Not directly — we generate Terraform, Bicep, and a checklist that you run through your normal pipeline. This keeps your provider credentials in your control and gives you a chance to review before applying." },
          { q: "Which providers are supported?", a: "Azure, AWS, and Google Cloud for conceptual architectures; the Enterprise-Scale landing zone is Azure-specific (with AWS and GCP equivalents on the roadmap)." },
          { q: "How accurate are the cost estimates?", a: "Each line shows a low / expected / high range using the current public SKU pricing for the chosen scale tier and 730 hours/month. Real costs vary with traffic and discounting." },
        ],
      },
    ],
  },
  {
    path: "/resources/guides",
    section: "Resources",
    title: "Guides",
    label: "Guides",
    column: "resources",
    description: "Opinionated playbooks for the hard architecture decisions.",
    sections: [
      {
        type: "features",
        kicker: "POPULAR GUIDES",
        title: "Read the ones that match your stage",
        items: [
          { icon: Server, title: "AKS vs App Service", body: "When to pick a managed PaaS, when Kubernetes is worth the complexity, and how Guided Sentry suggests the right one." },
          { icon: Database, title: "Picking the right database", body: "Postgres vs Cosmos vs DynamoDB vs Spanner — the framework we use to choose, scale tier by scale tier." },
          { icon: Network, title: "Hub-and-spoke 101", body: "Why a hub VNet exists, what should live in it (Firewall, Bastion, Gateway), and how spokes peer in." },
          { icon: Shield, title: "Baseline security controls", body: "The five Azure Policy assignments every landing zone should ship with — and what they protect against." },
          { icon: Wallet, title: "Cost optimization first 30 days", body: "Reserved instances vs autoscaling, picking storage tiers, the egress trap, and a checklist you can actually run." },
          { icon: Cpu, title: "Sizing for traffic", body: "A back-of-envelope method for converting MAU into vCPUs, DB IOPS, and cache memory." },
        ],
      },
    ],
  },
  {
    path: "/resources/changelog",
    section: "Resources",
    title: "Changelog",
    label: "Changelog",
    column: "resources",
    description: "Every shipped feature, every week. The latest is at the top.",
    sections: [
      {
        type: "changelog",
        releases: [
          {
            version: "v0.7",
            date: "Feb 2026",
            title: "Landing page redesign + teal accent",
            items: [
              "New marketing landing page with auto-scrolling marquee strip, interactive 3-step \"How it works\" section, and a complete footer.",
              "Switched the global accent color from indigo to teal (#2DD4BF) across the entire app.",
              "Updated all CTA buttons to use dark-on-teal text for AA contrast.",
              "Wired all 14 footer link destinations (Product · Resources · Company · Legal) with real content.",
            ],
          },
          {
            version: "v0.6",
            date: "Feb 2026",
            title: "Azure Enterprise-Scale landing zone",
            items: [
              "New /projects/:id/enterprise canvas. Click \"Enterprise\" in any project to generate a Microsoft CAF landing zone — tenant root MG, Platform/Landing-zones/Decomm/Sandbox children, subscriptions, RGs, 30+ resources.",
              "Hub-and-spoke networking layer with VNets, subnets, peerings, and the official Azure icon set bundled in /azure-icons/.",
              "Azure Policy assignments per MG, color-coded by severity (audit / append / deny).",
              "Enterprise Terraform export with hierarchical azurerm_management_group / azurerm_resource_group / per-resource blocks + policy assignments.",
            ],
          },
          {
            version: "v0.5",
            date: "Feb 2026",
            title: "Node detail panel + multi-cloud compare",
            items: [
              "Clicking a canvas node opens a sidebar with the AI-generated rationale, low/expected/high cost range, docs link, and a swap-with suggestion.",
              "New \"Compare\" button — re-prices the entire architecture across Azure, AWS, and GCP in one modal.",
              "PNG diagram export via Pillow with edges and tier-color borders.",
              "Markdown provisioning checklist export grouped by tier.",
            ],
          },
          {
            version: "v0.4",
            date: "Feb 2026",
            title: "AI generation + canvas",
            items: [
              "4-step requirements wizard backed by OpenAI GPT-5.",
              "Free-form draggable architecture canvas with debounced auto-save.",
              "Click-to-connect edges between nodes; click the × midpoint to delete.",
              "Provider switcher in the sidebar re-prices the existing graph for a different cloud.",
            ],
          },
          {
            version: "v0.1",
            date: "Feb 2026",
            title: "Foundation",
            items: [
              "Landing, Auth, and Dashboard pages.",
              "JWT cookie auth + per-user project scoping in MongoDB.",
              "Project CRUD with tier-dot mini-previews on the dashboard.",
              "Global design tokens, sonner toaster, lucide icons.",
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/resources/community",
    section: "Resources",
    title: "Community",
    label: "Community",
    column: "resources",
    description: "Architects and platform engineers using Guided Sentry to ship better infrastructure faster.",
    sections: [
      {
        type: "features",
        kicker: "WHERE WE HANG OUT",
        title: "Join the conversation",
        columns: 2,
        items: [
          { icon: Users, title: "Discord community", body: "Daily conversations on architecture, cost optimization, and Azure CAF best practices. Engineers from the team are active every day.", tag: "Most active" },
          { icon: Heart, title: "GitHub discussions", body: "Feature requests, bug reports, and template contributions. Star us if you're shipping with Guided Sentry." },
          { icon: BookOpen, title: "Monthly architecture review", body: "Office hours where we walk through community-submitted architectures live and answer questions on cloud design." },
          { icon: Globe, title: "X / Twitter", body: "Bite-sized announcements, threads on the latest cloud services, and the occasional cost-optimization war story." },
        ],
      },
      {
        type: "prose",
        kicker: "CONTRIBUTE",
        title: "Templates wanted",
        body: [
          "We're building out the template library with patterns submitted by the community. If you've built and run a particular architecture in production — and you can describe it well — we'd love to ship it as an official Guided Sentry template (with attribution).",
          "Email hello@guidedsentry.com with a short description and a screenshot of your canvas.",
        ],
      },
    ],
  },

  /* ============================= COMPANY ============================= */
  {
    path: "/company/about",
    section: "Company",
    title: "About Guided Sentry",
    label: "About",
    column: "company",
    description: "We're building the cloud planning tool we always wished existed.",
    sections: [
      {
        type: "prose",
        kicker: "OUR THESIS",
        title: "Cloud architecture should start with the product, not the services",
        body: [
          "Most cloud planning tools are built around the catalog of services — pick from a thousand boxes and figure out how they fit. That's backwards. The right starting point is the product you're trying to ship: what does it do, who uses it, and at what scale?",
          "Guided Sentry runs a structured conversation about your product, then maps it to an architecture draft with cost estimates and review-required IaC. The architect's job begins with a concrete plan instead of a blank canvas.",
          "We're a small team — opinionated, fast-shipping, and serious about both design and engineering rigor.",
        ],
      },
      {
        type: "features",
        kicker: "VALUES",
        title: "What we ship for",
        items: [
          { icon: Zap, title: "Ship the right thing", body: "Solve the hardest part of the problem in the simplest way. No abstraction theater." },
          { icon: Heart, title: "Care about the craft", body: "Polish every screen. Pixel-correct typography, motion, and contrast." },
          { icon: Shield, title: "Trustworthy by default", body: "Sensible defaults that lean toward security and observability, even on hobby projects." },
        ],
      },
    ],
  },
  {
    path: "/company/careers",
    section: "Company",
    title: "Work with us",
    label: "Careers",
    column: "company",
    description: "A small, opinionated team shipping fast across React, FastAPI, and OpenAI.",
    sections: [
      {
        type: "prose",
        kicker: "HOW WE WORK",
        title: "Tiny team, big surface area",
        body: [
          "Everyone is hands-on — there are no pure managers. You'll touch product strategy, code, design, and customer conversations every week.",
          "We work async-first across two timezones, with one in-person week per quarter. Strong written communication is a hard requirement; everything important is a document.",
        ],
      },
      {
        type: "roles",
        positions: [],
      },
    ],
  },
  {
    path: "/company/contact",
    section: "Company",
    title: "Get in touch",
    label: "Contact",
    column: "company",
    description: "We read every message. Usually reply within a day.",
    sections: [
      { type: "contact" },
      {
        type: "prose",
        kicker: "RESPONSIBLE DISCLOSURE",
        title: "Security researchers",
        body: [
          "Found a vulnerability? Email security@guidedsentry.com with a description and reproduction steps. We acknowledge within 24 hours and aim to ship a fix within 7 days for high-severity issues.",
          "Please don't publish details until we've confirmed a fix. We're happy to credit researchers in our changelog.",
        ],
      },
    ],
  },
  {
    path: "/company/security",
    section: "Company",
    title: "Security",
    label: "Security",
    column: "company",
    pill: "COUNSEL & SECURITY REVIEW DRAFT",
    description: "Current application safeguards and the questions still requiring operational verification before publication.",
    sections: [
      {
        type: "prose",
        kicker: "REVIEW STATUS",
        title: "This page is not a certification statement",
        body: [
          "Counsel and an operations owner must verify the claims and replace the marked placeholders before this draft is presented as an approved security statement. No SOC 2 certification, penetration-test schedule, or data-processing agreement is claimed here.",
          "[COUNSEL/OPS REVIEW: legal entity, security contact, hosting region, infrastructure providers, incident-notification process, and effective date.]",
        ],
      },
      {
        type: "prose",
        kicker: "APPLICATION CONTROLS",
        title: "Controls visible in the current application",
        body: [
          "Authenticated project endpoints check ownership before returning or changing a project. Share links are an explicit separate feature; anyone holding an active share link may be able to view the shared output.",
          "Passwords are hashed before storage. The application uses HttpOnly session cookies; production configuration enables Secure cookies. The deployment owner must verify TLS, database encryption, backups, secret handling, and access controls in the actual hosting environment.",
          "Generated Terraform and architecture recommendations are proposals, not automatically deployed resources. Review plans, permissions, and secrets before applying any export to a cloud account.",
        ],
      },
      {
        type: "prose",
        kicker: "INCIDENTS & ASSURANCE",
        title: "What still needs an operational policy",
        body: [
          "[COUNSEL/OPS REVIEW: security contact mailbox, vulnerability reporting instructions, incident response owner, breach-notification commitments, and customer communication channel.]",
          "[COUNSEL/OPS REVIEW: verify any audit, third-party assessment, compliance certification, and subprocessor claims before adding them. Do not infer compliance from generated architecture diagrams.]",
        ],
      },
    ],
  },

  /* ============================= LEGAL ============================= */
  {
    path: "/privacy",
    section: "Legal",
    title: "Privacy Policy",
    label: "Privacy Policy",
    column: "legal",
    pill: "COUNSEL REVIEW DRAFT",
    description: "A review draft describing Guided Sentry's data flows. Open items are marked for counsel and the operator.",
    sections: [
      {
        type: "prose",
        kicker: "REVIEW STATUS & CONTROLLER",
        title: "Who is responsible for this service",
        body: [
          "This is a counsel-review draft, not an approved final privacy notice. It must be reconciled with the live deployment and applicable law before publication as a policy.",
          "[COUNSEL REVIEW: legal entity and jurisdiction, business address, privacy contact email, effective date, controller/processor roles, and lawful bases where applicable.]",
        ],
      },
      {
        type: "prose",
        kicker: "DATA & PURPOSES",
        title: "Information the product handles",
        body: [
          "Account and access data may include email, a password hash, authentication and recovery tokens, plan status, and billing identifiers. Project data includes names, architecture requirements, interview messages, generated designs, cost estimates, and exports. Early-access and support forms collect the information you submit.",
          "These records support account access, product operation, architecture generation, billing, support, abuse prevention, and service reliability. Browser cookies are used for sign-in. [COUNSEL REVIEW: confirm any analytics, marketing cookies, error telemetry, and corresponding choices in the live deployment.]",
          "Avoid submitting passwords, cloud keys, regulated personal information, or other secrets in project prompts. The product does not need such material to suggest an architecture.",
        ],
      },
      {
        type: "prose",
        kicker: "SERVICE PROVIDERS",
        title: "Where information may go",
        body: [
          "The OpenAI API processes relevant project prompts and architecture context when you request AI features. Hosting and database providers store service data. Stripe may process subscription and payment information, and Resend may deliver service emails when these integrations are enabled.",
          "A project owner may deliberately create a share link; recipients with that link can access the shared view. We do not describe model processing or a user-created share link as private to the account owner alone.",
          "[COUNSEL/OPS REVIEW: if Sentry.io error tracking is enabled, disclose it as a monitoring provider and verify its data, location, and retention terms. Confirm the actual vendor names, locations, international-transfer mechanism, subprocessor list, payment-data flow, and each provider's retention terms. Do not claim MongoDB Atlas or a private network without deployment evidence.]",
        ],
      },
      {
        type: "prose",
        kicker: "RETENTION & DELETION",
        title: "How long information remains",
        body: [
          "Users can delete individual projects and can request account deletion through the application. Account deletion removes account-owned records in the application database after password re-authentication; it does not itself cancel an external Stripe subscription or instantly remove provider backups and records held under separate obligations.",
          "[COUNSEL/OPS REVIEW: approved retention periods and deletion windows for prompts, generated projects, account and billing records, operational logs, backups, and third-party processors; explain legal holds and backup expiry. No specific duration has been approved yet.]",
        ],
      },
      {
        type: "prose",
        kicker: "YOUR RIGHTS",
        title: "Requests and contact",
        body: [
          "Depending on your location, you may have rights to access, correct, delete, or export personal data, or object to certain processing. The applicable procedure and response period depend on the governing law.",
          "[COUNSEL REVIEW: verified privacy contact, request-verification process, applicable rights and response deadlines, and how users may appeal or complain to a regulator.]",
        ],
      },
    ],
  },
  {
    path: "/terms",
    section: "Legal",
    title: "Terms of Service",
    label: "Terms of Service",
    column: "legal",
    pill: "COUNSEL REVIEW DRAFT",
    description: "A draft service agreement for review, with commercial and jurisdictional choices left explicit.",
    sections: [
      {
        type: "prose",
        kicker: "REVIEW STATUS & PARTIES",
        title: "Agreement details to approve",
        body: [
          "This is a counsel-review draft and is not represented as an approved agreement. [COUNSEL REVIEW: contracting legal entity, address, effective date, governing law, venue, age and authority requirements, and contact for legal notices.]",
          "An account holder is responsible for account access and for ensuring submitted project information may lawfully be used with the service. Do not submit credentials or secrets in architecture prompts.",
        ],
      },
      {
        type: "prose",
        kicker: "SERVICE & ACCEPTABLE USE",
        title: "What the product provides",
        body: [
          "Guided Sentry helps plan cloud architectures and export design artifacts, including infrastructure-as-code files. An export is not an authorization to access a cloud account or deploy resources. Any future connected deployment must require separate, explicit user approval.",
          "Use the service lawfully and do not interfere with other users, attempt unauthorized access, submit harmful code or secrets, or evade technical usage limits. [COUNSEL REVIEW: acceptable-use enforcement, suspension process, notice, and appeal terms.]",
        ],
      },
      {
        type: "prose",
        kicker: "AI-GENERATED CONTENT",
        title: "Review before deployment",
        body: [
          "Architectures, cost estimates, and IaC are generated from user input and may be incomplete, insecure, or unsuitable for a specific environment. Have a qualified person validate the design, provider permissions, costs, compliance requirements, and Terraform plan before production use.",
          "Cost figures are estimates, not quotes or spending caps. Cloud-provider prices and actual usage can change. [COUNSEL REVIEW: output ownership/license, third-party rights, and any professional-services disclaimer.]",
        ],
      },
      {
        type: "prose",
        kicker: "PLANS & BILLING",
        title: "Subscriptions and cancellation",
        body: [
          "The application may offer free and paid plans with usage limits shown at purchase. A payment processor handles checkout and subscriptions; account deletion in Guided Sentry does not automatically cancel a paid subscription. Use the billing portal or contact support for cancellation assistance.",
          "[COUNSEL/PRODUCT REVIEW: final plan entitlements, recurring fees, taxes, renewals, refunds, price changes, trials, seat minimums, and cancellation effective date. Align this section after the planned pricing migration.]",
        ],
      },
      {
        type: "prose",
        kicker: "DATA & ENDING SERVICE",
        title: "Projects, sharing, and termination",
        body: [
          "You control the project content you submit and may export available outputs. Share links expose the selected project view to people with the link; disable a link if it should no longer be accessible. See the Privacy Policy draft for the data-flow and deletion review items.",
          "[COUNSEL REVIEW: content license needed to operate AI features, confidentiality duties, account suspension/termination, data export window, and post-termination retention.]",
        ],
      },
      {
        type: "prose",
        kicker: "WARRANTIES & DISPUTES",
        title: "Commercial terms for counsel",
        body: [
          "[COUNSEL REVIEW: service availability commitments, warranties and disclaimers, liability cap and exclusions, indemnities, dispute resolution, governing law, and mandatory consumer rights. No numeric liability cap is approved in this draft.]",
        ],
      },
    ],
  },
];

export const pagesByColumn = (col) => STATIC_PAGES.filter((p) => p.column === col);
